import { CollapsibleFilters } from '../../common/CollapsibleFilters';
import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControl,
  FormLabel,
  Input,
  Dropdown,
  Menu,
  MenuButton,
  MenuItem,
  Modal,
  ModalDialog,
  Option,
  Select,
  Stack,
  Typography,
} from "@mui/joy";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../../lib/api";
import type { EmailCampaign } from "../../../types/marketing";
import { ResponsiveTable } from "../../common/ResponsiveTable";
import { CampaignComposer } from "./CampaignComposer";
import { CampaignReview } from "./CampaignReview";
import { EmailReport } from "./EmailReport";
import { emailError, type EmailPage } from "./emailWorkflows";
export function CampaignsTab() {
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState<Partial<EmailCampaign> | null>(null);
  const [review, setReview] = useState<{
    campaign: EmailCampaign;
    action: "send-now" | "resume" | "schedule" | "draft";
  } | null>(null);
  const [report, setReport] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const cache = useQueryClient();
  const query = useQuery({
    queryKey: ["email-campaigns", search, status, from, to, page],
    queryFn: async () =>
      (
        await api.get<EmailPage<EmailCampaign>>(
          `/api/marketing/email-campaigns/?${new URLSearchParams({ search, status, date_from: from, date_to: to, page: String(page) })}`,
        )
      ).data,
    refetchInterval: 15000,
  });
  const run = async (
    campaign: EmailCampaign,
    action: "pause" | "cancel" | "duplicate" | "delete",
  ) => {
    if (
      action === "cancel" &&
      !window.confirm(
        `Cancel remaining delivery for “${campaign.name}”? Already accepted emails cannot be recalled.`,
      )
    )
      return;
    if (
      action === "delete" &&
      !window.confirm(`Delete draft “${campaign.name}”?`)
    )
      return;
    setBusy(true);
    setError("");
    try {
      if (action === "delete") await api.deleteEmailCampaign(campaign.id);
      else
        await api.post(
          `/api/marketing/email-campaigns/${campaign.id}/${action}/`,
          {},
        );
      setNotice(
        action === "duplicate"
          ? "A new draft copy is ready."
          : action === "pause"
            ? "Campaign paused."
            : action === "cancel"
              ? "Campaign canceled."
              : "Draft deleted.",
      );
      await cache.invalidateQueries({ queryKey: ["email-campaigns"] });
    } catch (cause) {
      setError(emailError(cause));
    }
    setBusy(false);
  };
  return (
    <Stack spacing={2}>
      <Button
          onClick={() =>
            setEditing({
              name: "",
              template: null,
              subject: "",
              html_content: "",
              audience_type: "all",
              audience_contact_type: "",
              from_name: "",
              reply_to: "",
            })
          }
        >
          New campaign
        </Button>
<CollapsibleFilters><Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
        <Input
          aria-label="Search campaigns"
          placeholder="Search campaigns"
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            setPage(1);
          }}
          sx={{ flex: 1 }}
        />
        <Select
          aria-label="Campaign status"
          value={status}
          onChange={(_, value) => {
            setStatus(value || "");
            setPage(1);
          }}
        >
          <Option value="">All statuses</Option>
          {[
            "draft",
            "scheduled",
            "sending",
            "sent",
            "partial",
            "paused",
            "failed",
            "canceled",
          ].map((value) => (
            <Option key={value} value={value}>
              {value}
            </Option>
          ))}
        </Select>

      </Stack></CollapsibleFilters>
      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
        <FormControl>
          <FormLabel>Created from</FormLabel>
          <Input
            type="date"
            value={from}
            onChange={(event) => {
              setFrom(event.target.value);
              setPage(1);
            }}
          />
        </FormControl>
        <FormControl>
          <FormLabel>Created through</FormLabel>
          <Input
            type="date"
            value={to}
            onChange={(event) => {
              setTo(event.target.value);
              setPage(1);
            }}
          />
        </FormControl>
      </Box>
      {error && <Alert color="danger">{error}</Alert>}
      {notice && <Alert color="success">{notice}</Alert>}
      {query.isError && (
        <Alert
          color="danger"
          endDecorator={<Button onClick={() => query.refetch()}>Retry</Button>}
        >
          Campaigns could not be loaded.
        </Alert>
      )}
      <ResponsiveTable
        rows={query.data?.results || []}
        getRowKey={(campaign) => campaign.id}
        columns={[
          {
            key: "name",
            header: "Campaign",
            mobileVariant: "header",
            render: (c) => (
              <Typography level="title-sm" sx={{ overflowWrap: "anywhere" }}>
                {c.name}
              </Typography>
            ),
          },
          {
            key: "status",
            header: "Status",
            render: (c) => (
              <Chip
                size="sm"
                color={
                  c.status === "sent"
                    ? "success"
                    : ["partial", "failed"].includes(c.status)
                      ? "warning"
                      : "neutral"
                }
              >
                {c.status}
              </Chip>
            ),
          },
          {
            key: "recipients",
            header: "Accepted / audience",
            render: (c) => `${c.sent_count} / ${c.total_recipients}`,
          },
          {
            key: "schedule",
            header: "Scheduled",
            render: (c) =>
              c.scheduled_at ? new Date(c.scheduled_at).toLocaleString() : "—",
          },
        ]}
        actionsWidth="220px"
        rowActions={(campaign) => (
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            {["draft", "partial", "failed", "paused"].includes(
              campaign.status,
            ) ? (
              <Button
                size="sm"
                variant="outlined"
                onClick={() =>
                  setReview({
                    campaign,
                    action:
                      campaign.status === "paused" ? "resume" : "send-now",
                  })
                }
              >
                {campaign.status === "draft"
                  ? "Review & send"
                  : campaign.status === "paused"
                    ? "Review & resume"
                    : "Review & retry"}
              </Button>
            ) : (
              <Button
                size="sm"
                variant="outlined"
                onClick={() => setReport(campaign.id)}
              >
                Report
              </Button>
            )}
            <Dropdown>
              <MenuButton size="sm" variant="plain">
                More
              </MenuButton>
              <Menu>
                <MenuItem onClick={() => setReport(campaign.id)}>
                  Campaign report
                </MenuItem>
                <MenuItem
                  onClick={() => setReview({ campaign, action: "draft" })}
                >
                  Review content and audience
                </MenuItem>
                {campaign.status === "draft" && (
                  <MenuItem onClick={() => setEditing(campaign)}>
                    Edit draft
                  </MenuItem>
                )}
                {campaign.status === "draft" && (
                  <MenuItem
                    onClick={() => setReview({ campaign, action: "schedule" })}
                  >
                    Schedule campaign
                  </MenuItem>
                )}
                {["sending", "scheduled"].includes(campaign.status) && (
                  <MenuItem
                    disabled={busy}
                    onClick={() => run(campaign, "pause")}
                  >
                    Pause delivery
                  </MenuItem>
                )}
                {!["sent", "canceled"].includes(campaign.status) && (
                  <MenuItem
                    disabled={busy}
                    onClick={() => run(campaign, "cancel")}
                  >
                    Cancel remaining delivery
                  </MenuItem>
                )}
                <MenuItem
                  disabled={busy}
                  onClick={() => run(campaign, "duplicate")}
                >
                  Duplicate into a draft
                </MenuItem>
                {campaign.status === "draft" && (
                  <MenuItem
                    color="danger"
                    disabled={busy}
                    onClick={() => run(campaign, "delete")}
                  >
                    Delete draft
                  </MenuItem>
                )}
              </Menu>
            </Dropdown>
          </Stack>
        )}
        pagination={{
          pageSize: 20,
          page,
          totalCount: query.data?.count || 0,
          onPageChange: setPage,
          itemNoun: "campaigns",
        }}
        emptyState={
          <Typography>
            {query.isPending
              ? "Loading campaigns…"
              : query.isError
                ? "Campaigns unavailable."
                : "No matching campaigns."}
          </Typography>
        }
      />
      {editing && (
        <CampaignComposer
          key={editing.id || "new"}
          initial={editing}
          onClose={() => setEditing(null)}
        />
      )}
      {review && (
        <Modal open onClose={() => setReview(null)}>
          <ModalDialog
            sx={{
              width: 900,
              maxWidth: "96vw",
              maxHeight: "94vh",
              overflowY: "auto",
            }}
          >
            <Stack direction="row" justifyContent="space-between">
              <Typography level="h4">Review {review.campaign.name}</Typography>
              <Button variant="plain" onClick={() => setReview(null)}>
                Close
              </Button>
            </Stack>
            <CampaignReview
              id={review.campaign.id}
              action={review.action}
              onComplete={() => setReview(null)}
            />
          </ModalDialog>
        </Modal>
      )}
      {report !== null && (
        <EmailReport campaignId={report} onClose={() => setReport(null)} />
      )}
    </Stack>
  );
}
